<template>
<div
  v-if="filters?.length"
  class="catalog-selected-filters"
>
  <template v-for="(selectedFilter) in selectedFilters">
    <div class="tooltip">
      <div class="tooltip__item" @click="() => clickSelectedVariant(selectedFilter)">
        {{ selectedFilter.variantLabel }}
        <img
          src="~/assets/img/svg/common/close.svg"
          alt="закрыть"
        />
      </div>
      <div class="tooltip__hover">
        {{ selectedFilter.filterLabel }}
      </div>
    </div>
  </template>
</div>
</template>

<script setup lang="ts">

import type { IFilter } from '#sitis/internal/controllers/filters/models/Filter';
import { filterWlBedFunctionSort, filterWlBedParseWidthLengthSlug } from '~/utils/filterWlBed';
import type { LooseRequired } from '@vue/shared';

interface ISelectedFilterItem {
  filterSlug: string;
  filterLabel: string;
  variantSlug: string;
  variantLabel: string;
  filterTemplate?: string;
}
interface IProps {
  filters: IFilter[] | null;
  selected: any,
  filterPrice: any,
}
const props = withDefaults(defineProps<IProps>(), {
  filters: null,
  selected: null,
  filterPrice: null,
});
const emits = defineEmits(['change']);
const {
  $numberFormat
} = useNuxtApp();

const selectedFilters = computed(() => {
  const _selected = JSON.parse(JSON.stringify({...props.selected}));
  let list: ISelectedFilterItem[] = [];

  if (_selected?.priceFrom || _selected?.priceTo) {
    const priceTo = Object.keys(_selected?.priceTo || {})[0] || '';
    const priceFrom = Object.keys(_selected?.priceFrom || {})[0] || '';

    list.push({
      filterSlug: "price",
      filterLabel: "Стоимость",
      variantSlug: "price",
      variantLabel: `Стоимость от ${ $numberFormat(priceFrom || props.filterPrice.min) } до ${ $numberFormat(priceTo || props.filterPrice.max) }`
    });

    delete _selected.priceTo;
    delete _selected.priceFrom;
  }

  Object.keys((_selected || {})).map((filterSlug: string) => {
    const filter: IFilter | undefined = props.filters!.find((filter: IFilter) => filter.slug === filterSlug);
    const variants: string[] = Object.keys(_selected[filterSlug]);

    if (['wl_bed'].includes(filterSlug)) {
      let _selectedWidth: (string | undefined)[] = [];
      let _selectedLength: (string | undefined)[] = [];
      variants.map((key: string) => {
        const [_width, _length] = filterWlBedParseWidthLengthSlug(key);
        _selectedWidth.push(_width);
        _selectedLength.push(_length);
      });
      _selectedWidth = filterWlBedFunctionSort(_selectedWidth);
      _selectedLength = filterWlBedFunctionSort(_selectedLength);

      list.push({
        filterSlug: filterSlug,
        filterLabel: filter?.nameForFilter || filter?.name || '',

        variantSlug: 'full',
        variantLabel: `Ширина спального места от ${_selectedWidth[0]} до ${_selectedWidth.pop()} ${filter?.unit || 'мм'}`
      });
      list.push({
        filterSlug: filterSlug,
        filterLabel: filter?.nameForFilter || filter?.name || '',

        variantSlug: 'full',
        variantLabel: `Длина спального места от ${_selectedLength[0]} до ${_selectedLength.pop()} ${filter?.unit || 'мм'}`
      });
    }
    else if (['checkbox_list_with_range'].includes(filter?.filterTemplate || '')) {
      const [minValue, maxValue] = (variants[0] || '').split(':');
      list.push({
        filterSlug: filterSlug,
        filterLabel: filter?.nameForFilter || filter?.name || '',

        variantSlug: 'full',
        variantLabel: `${filter?.name} ${minValue} до ${maxValue}`,

        filterTemplate: filter?.filterTemplate
      });
    }
    else {
      variants.map((variantSlug: string) => {
        list.push({
          filterSlug: filterSlug,
          filterLabel: filter?.nameForFilter || filter?.name || '',

          variantSlug: variantSlug,
          variantLabel: String((filter?.variants || []).find((variant) => variant.slug === variantSlug)?.label || '')
        })
      });
    }
  });

  return [...list]
    .filter((t: any) => {
      return Boolean(t.variantLabel || t.filterLabel)
    })
});

const clickSelectedVariant = (item: ISelectedFilterItem) => {
  let _selected = JSON.parse(JSON.stringify({...props.selected}));

  if (item.filterSlug === 'wl_bed') {
    delete _selected['wl_bed'];
  }
  if (item.filterTemplate === 'checkbox_list_with_range') {
    delete _selected[item.filterSlug];
  }
  else if (item.filterSlug === 'price') {
    delete _selected['priceTo'];
    delete _selected['priceFrom'];
  }
  else {
    if (_selected?.[item.filterSlug]?.[item.variantSlug]) {
      delete _selected[item.filterSlug][item.variantSlug];
    }
    if (Object.keys(_selected[item.filterSlug] || {}).length <= 0) {
      delete _selected[item.filterSlug]
    }
  }

  emits('change', _selected);
}
</script>

<style lang="scss">
.catalog-selected-filters {
  display: flex;
  flex-wrap: wrap;
  margin-top: -16px;
  margin-left: -16px;

  .tooltip__item {
    display: flex;
    align-items: center;
    margin-top: 16px;
    margin-left: 16px;
    border-radius: 8px;
    background: #F4F3F1;
    padding: 8.5px 24px;
    box-sizing: border-box;

    font-size: 16px;
    font-weight: 500;
    line-height: 140%;
    color: #000;

    img {
      width: 20px;
      height: 20px;
      margin-left: 10px;
    }
  }
  .tooltip__hover {
    right: initial;
    left: 50%;
    transform: translateX(-50%);
    filter: drop-shadow(0px 4px 7.1px rgba(0, 0, 0, 0.05));
    padding: 8px 12px;
    text-align: center;
    background: #FFFFFF;
    border-radius: 4px;

    &:after {
      border-right: 10px solid transparent;
      border-top: 15px solid #FFFFFF;
      bottom: -25px;
      right: initial;
      left: 50%;
      transform: translateX(-50%);
    }
  }
}
@media (max-width: 1023px) {
  .catalog-selected-filters {
    margin-top: -8px;
    margin-left: -8px;

    .tooltip__item {
      margin-top: 8px;
      margin-left: 8px;
      font-size: 12px;
      padding: 6px 12px;

      img {
        width: 16px;
        height: 16px;
        margin-left: 4px;
      }
    }
  }
}
</style>
